import { forwardRef, useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { listCases, listRecentReadings, listThreads } from "../api"
import type { RecentReading, ThreadRow } from "../contract"
import { requireLogin } from "@/lib/authGate"
import { NiraHistory, loadSimDrafts, type HistoryScope, type ReadingsState, type SimDraft } from "./NiraHistory"
import { track } from "@/lib/analytics"
import { GROUPS, groupThreads } from "./history"
import { Beam } from "../ds"
import { sideWords, stateName, useReaderContext } from "../readerContext"
import { ClockIcon, CloseIcon, PlusIcon } from "./icons"
import { ThemeButton } from "./ThemeButton"

interface RailProps {
  threadId: string
  histOpen: boolean
  onHist: (open: boolean) => void
  /** Changes when a turn finishes, so the history list refreshes. */
  refreshKey: number
  /** Where the logo and New chat go (default "/ask"). */
  homeTo?: string
  /** Where the logo alone goes, when it differs from New chat (the simulators: Nira's home). */
  logoTo?: string
  /** Where the history list comes from (default: NIRA's threads). */
  loadThreads?: () => Promise<ThreadRow[]>
  /** Route of one history row (default: /nira/:id). */
  threadPath?: (id: string) => string
  /** When set, each history row gets a delete action; it resolves once the case is deleted. Absent: no button. */
  onDelete?: (id: string) => Promise<unknown>
  /** Text overrides for other pages; NIRA's wording is the default. */
  labels?: { home?: string; untitled?: string; empty?: string }
  /** Only the history drawer: the page's top bar carries the menu button and the mark. */
  drawerOnly?: boolean
  /** Accepted for callers; the drawer no longer lists workspaces, so it is not used. */
  caseId?: string
  /** Workspaces and workspace-bound history need an account; off for a guest (Workspaces asks them to sign in). */
  showMatters?: boolean
  /** The feature whose history the drawer shows (its sessions only). Default: research. */
  scope?: HistoryScope
}

/** The drawer's New button, per feature. Notice and appeal pages are workspace pages: they start a new question. */
/** Where "New question" goes: the fresh-question page. The answer page's bar button uses it too. */
export const NEW_QUESTION_TO = "/ask"
const NEW_LABEL: Record<HistoryScope, string> = { research: "New question", notice: "New question", appeal: "New question", oio: "New OIO draft", oia: "New OIA draft" }

const ICON = { viewBox: "0 0 20 20", width: 18, height: 18, fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, className: "hico" } as const
const NewIcon = () => <svg {...ICON}><path d="M10 4.5v11M4.5 10h11" /></svg>
/** A folder with a second sheet behind it: the place a case's sessions and papers are kept together. */
const WorkspacesIcon = () => <svg {...ICON}><path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h3.2l1.6 1.8h6.2A1.5 1.5 0 0 1 17 8.3v6.2a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5z" /><path d="M3 9.5h14" /></svg>

const TIP_SIGNED_IN = "Sessions you start inside a workspace live here, not in Recents"
const TIP_GUEST = "Sign in to keep files and sessions together in workspaces"

/**
 * A small tooltip for one drawer row: after 300ms on hover, at once on keyboard focus, gone on Esc, blur or leave.
 * It is fixed to the viewport (the drawer scrolls and would clip it): right of the row, or below it on narrow screens.
 */
function NavTip({ text, children }: { text: string; children: (p: { id: string; ref: React.RefObject<HTMLAnchorElement & HTMLButtonElement>; onMouseEnter: () => void; onMouseLeave: () => void; onFocus: () => void; onBlur: () => void; onKeyDown: (e: React.KeyboardEvent) => void }) => ReactNode }) {
  const id = useId()
  const ref = useRef<HTMLAnchorElement & HTMLButtonElement>(null)
  const timer = useRef<number>()
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null)
  const show = useCallback(() => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const below = window.innerWidth < 640
    setPos(below ? { left: r.left, top: r.bottom + 6, below } : { left: r.right + 8, top: r.top + r.height / 2, below })
  }, [])
  const hide = useCallback(() => { window.clearTimeout(timer.current); setPos(null) }, [])
  useEffect(() => () => window.clearTimeout(timer.current), [])
  return (
    <>
      {children({
        id, ref,
        onMouseEnter: () => { window.clearTimeout(timer.current); timer.current = window.setTimeout(show, 300) },
        onMouseLeave: hide,
        onFocus: () => { window.clearTimeout(timer.current); show() },
        onBlur: hide,
        onKeyDown: (e) => { if (e.key === "Escape" && pos) { e.stopPropagation(); hide() } },
      })}
      <span id={id} role="tooltip" className="hnav-tip" data-below={pos?.below ? "" : undefined} hidden={!pos} style={pos ? { left: pos.left, top: pos.top } : undefined}>{text}</span>
    </>
  )
}

/** True on the Workspaces list and on any one workspace. */
export const inWorkspaces = (path: string): boolean => /^\/workspaces?(\/|$)/.test(path)

/** 56px icon rail (home mark, new chat, history, theme) and the 260px history list. */
export const Rail = forwardRef<HTMLElement, RailProps>(function Rail({ threadId, histOpen, onHist, refreshKey, homeTo = NEW_QUESTION_TO, logoTo, loadThreads = listThreads, threadPath = (id) => `/nira/${encodeURIComponent(id)}`, onDelete, labels, drawerOnly, showMatters = true, scope = "research" }, histRef) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [wsCount, setWsCount] = useState<number | null>(null)
  // The workspace count is one cheap list call, made when the drawer opens for a signed-in reader; no count if it fails.
  useEffect(() => {
    if (!drawerOnly || !histOpen || !showMatters) return
    let live = true
    try {
      listCases().then((r) => live && Array.isArray(r) && setWsCount(r.length)).catch(() => {})
    } catch { /* no count */ }
    return () => {
      live = false
    }
  }, [drawerOnly, histOpen, refreshKey, showMatters])
  const [rows, setRows] = useState<ThreadRow[]>([])
  const [sims, setSims] = useState<SimDraft[]>([])
  // The drawer's Recents cover every flow: both simulators' drafts, and the notice and appeal readings
  // across the reader's workspaces. Fetched only in the drawer, only once opened, only for a signed-in reader.
  useEffect(() => {
    if (!drawerOnly || !histOpen || !showMatters) return
    let live = true
    loadSimDrafts(20).then((r) => live && setSims(r))
    return () => {
      live = false
    }
  }, [drawerOnly, histOpen, refreshKey, showMatters])
  const [readings, setReadings] = useState<RecentReading[]>([])
  const [readingsState, setReadingsState] = useState<ReadingsState>("loading")
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    if (!drawerOnly || !histOpen || !showMatters) return
    let live = true
    setReadingsState("loading")
    try {
      listRecentReadings("all", 20)
        .then((r) => { if (live) { setReadings(Array.isArray(r) ? r : []); setReadingsState("ready") } })
        .catch(() => live && setReadingsState("error"))
    } catch { setReadingsState("error") }
    return () => {
      live = false
    }
  }, [drawerOnly, histOpen, refreshKey, showMatters, retry])
  useEffect(() => {
    if (drawerOnly && histOpen) track("history_opened", {})
  }, [drawerOnly, histOpen])

  // Research threads feed every drawer's Recents; the readings and drafts come from their own lists.
  const wantsThreads = true
  useEffect(() => {
    if (!wantsThreads) return
    let live = true
    loadThreads()
      .then((r) => live && setRows(Array.isArray(r) ? r : []))
      .catch(() => live && setRows([]))
    return () => {
      live = false
    }
  }, [refreshKey, loadThreads, wantsThreads])

  const [deleteError, setDeleteError] = useState("")
  const remove = async (row: ThreadRow) => {
    if (!onDelete || !window.confirm("Delete this draft? This can't be undone.")) return
    setDeleteError("")
    try {
      await onDelete(row.id)
    } catch {
      setDeleteError("Couldn't delete that case. Try again.")
      return
    }
    setRows((cur) => cur.filter((r) => r.id !== row.id))
    if (row.id === threadId) navigate(homeTo)
  }

  const groups = groupThreads(rows)
  const ctx = useReaderContext((s) => (threadId ? s.threads[threadId] : undefined) ?? s.preferred)
  return (
    <>
      {drawerOnly ? null : (
        <aside className="rail" aria-label="Primary">
          <Link className="logo" to={logoTo ?? homeTo} aria-label={labels?.home ?? "Nira home"} title="Home"><Beam state="idle" label={null} /></Link>
          <button className="ib" type="button" aria-label="New chat" onClick={() => navigate(homeTo)}><PlusIcon /></button>
          <button className="ib" type="button" aria-label="Question history" aria-expanded={histOpen} aria-controls="nira-hlist" onClick={() => onHist(!histOpen)}>
            <ClockIcon />
          </button>
          <span className="sp" />
          <ThemeButton />
        </aside>
      )}
      <aside className={`hlist${histOpen ? " open" : ""}`} id="nira-hlist" aria-label="Question history" ref={histRef}>
        <div className="hhead">
          {drawerOnly ? (
            <Link className="hlogo" to={homeTo} aria-label="Nira home" onClick={() => onHist(false)}>
              <Beam state="idle" label={null} />
              <span>nira</span>
            </Link>
          ) : (
            <span className="lab">History</span>
          )}
          <button className="ib" type="button" data-closehist aria-label="Close history" onClick={() => onHist(false)}><CloseIcon /></button>
        </div>
        {drawerOnly ? (
          <button type="button" className="hnew" onClick={() => { onHist(false); navigate(scope === "oio" || scope === "oia" ? `/simulator?order=${scope}` : homeTo) }}>
            <NewIcon />
            <span>{NEW_LABEL[scope]}</span>
          </button>
        ) : null}
        {drawerOnly ? (
          showMatters ? (
            <NavTip text={TIP_SIGNED_IN}>
              {(t) => (
                <Link className="hnav" to="/workspaces" aria-describedby={t.id} ref={t.ref as never} onMouseEnter={t.onMouseEnter} onMouseLeave={t.onMouseLeave} onFocus={t.onFocus} onBlur={t.onBlur} onKeyDown={t.onKeyDown}
                  aria-current={inWorkspaces(pathname) ? "page" : undefined} onClick={() => { track("workspaces_opened", { from: "history" }); onHist(false) }}>
                  <WorkspacesIcon />
                  <span className="hnav-t">Workspaces</span>
                  {wsCount !== null ? <span className="hnav-n" aria-hidden="true">{wsCount}</span> : null}
                </Link>
              )}
            </NavTip>
          ) : (
            <NavTip text={TIP_GUEST}>
              {(t) => (
                <button type="button" className="hnav" aria-describedby={t.id} ref={t.ref} onMouseEnter={t.onMouseEnter} onMouseLeave={t.onMouseLeave} onFocus={t.onFocus} onBlur={t.onBlur} onKeyDown={t.onKeyDown}
                  onClick={() => { onHist(false); requireLogin("login_required", "/workspaces") }}>
                  <WorkspacesIcon />
                  <span className="hnav-t">Workspaces</span>
                </button>
              )}
            </NavTip>
          )
        ) : null}
        {drawerOnly && histOpen ? (
          <NiraHistory
            threads={rows} sims={showMatters ? sims : []} readings={showMatters ? readings : []} readingsState={showMatters ? readingsState : "ready"} onRetry={() => setRetry((n) => n + 1)} scope={scope} threadId={threadId}
            onClose={() => onHist(false)} ago={(iso) => ago(iso)}
            onGone={(kind, id) => (kind === "thread" ? setRows((cur) => cur.filter((r) => r.id !== id)) : setSims((cur) => cur.filter((m) => m.id !== id)))}
          />
        ) : null}
        {!drawerOnly && rows.length === 0 ? <p className="hempty">{labels?.empty ?? "Your past questions will show here."}</p> : null}
        {deleteError ? <p className="hempty" role="alert">{deleteError}</p> : null}
        {drawerOnly ? null : GROUPS.filter((g) => groups[g].length).map((g) => (
          <div key={g}>
            <h2>{g}</h2>
            <ul>
              {groups[g].map((r) => (
                <li key={r.id}>
                  <button type="button" className="hi" aria-current={r.id === threadId ? "true" : undefined}
                    title={r.title || undefined}
                    onClick={() => { onHist(false); navigate(threadPath(r.id)) }}>
                    <span className="hi-t">{r.title || labels?.untitled || "New research"}</span>
                    {drawerOnly && r.updated_at ? <span className="hi-d">{ago(r.updated_at)}</span> : null}
                  </button>
                  {onDelete ? (
                    <button type="button" className="hi-del" aria-label={`Delete ${r.title || labels?.untitled || "case"}`} onClick={() => { void remove(r) }}>
                      <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10" /></svg>
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {drawerOnly ? (
          <div className="hfoot">{stateName(ctx.state_code) || ctx.state} · {sideWords(ctx.side)}</div>
        ) : null}
      </aside>
    </>
  )
})

const DAY_MS = 864e5
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** A history row's age, short: "now", "3h", "Tue", "12 Oct". */
export function ago(iso: string, now: Date = new Date()): string {
  const t = new Date(iso)
  const ms = now.getTime() - t.getTime()
  if (Number.isNaN(ms)) return ""
  if (ms < 36e5) return ms < 6e4 ? "now" : `${Math.floor(ms / 6e4)}m`
  if (ms < DAY_MS && t.getDate() === now.getDate()) return `${Math.floor(ms / 36e5)}h`
  if (ms < 7 * DAY_MS) return t.toLocaleDateString("en-GB", { weekday: "short" })
  return `${t.getDate()} ${MONTHS[t.getMonth()]}`
}
